import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, Pressable, Modal, ScrollView,
  TextInput, Platform,
} from 'react-native';
import { useSelector } from 'react-redux';
import Svg, { Path } from 'react-native-svg';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { DELIVERY_RPM_ICON_OPTIONS } from './components/deliveryRpmIcons';
import HarborDataTable from '../../../harbor/HarborDataTable';
import {
  HaulbackReasonCode,
  TPL_STATUSES, DUO_STATUSES, REASON_CODE_CATEGORIES,
  haulbackFromDTO,
} from './types';
import MultiSelectDropdown from './components/MultiSelectDropdown';
import ConfirmCancelModal from './components/ConfirmCancelModal';
import { t } from './i18n';
import { ListLanguageToggle } from '../PerfectTruckManagement/EquipmentConfig';
import { EquipmentLanguage } from '../PerfectTruckManagement/types';
import FontFamily from '../../../../constants/fonts';
import { useToast } from '../../../../components/LhToastContainer';
import { AdminColors } from '../../constants/adminColors';
import { RootState } from '../../../../store';
import {
  fetchHaulbacks,
  fetchHaulbackById,
  createHaulback,
  updateHaulback,
  toggleHaulback,
  parseError,
} from '../../../../api/deliveryRpmApi';

// ─── Types ────────────────────────────────────────────────────────────────────

type SortDir = 'asc' | 'desc';
type ColFilters = Record<string, string[]>;

// Maps UI column keys to backend sortBy field names
const HAULBACK_SORT_FIELD_MAP: Record<string, string> = {
  reasonCode: 'reasonCode',
  displayOrder: 'displayOrder',
  locationTypes: 'locationType',
  orgTypes: 'orgType',
  tplStatuses: 'threePlStatus',
  duoStatuses: 'duoStatus',
  active: 'isActive',
};

interface FormState {
  reasonCode: string;
  displayOrder: string;
  reasonCodeCategory: 'Delivery' | 'Equipment' | 'Sales' | 'Warehouse';
  locationTypes: string[];
  orgTypes: string[];
  tplStatuses: string[];
  duoStatuses: string[];
  pictureRequired: boolean;
  pictureOptional: boolean;
  pictureTitle: string;
  textFieldRequired: boolean;
  textFieldOptional: boolean;
  textFieldTitle: string;
  supervisorNotificationWarning: boolean;
  supervisorAutomaticNotification: boolean;
  icon: string;
}

const EMPTY_FORM: FormState = {
  reasonCode: '',
  displayOrder: '',
  reasonCodeCategory: 'Delivery',
  locationTypes: [],
  orgTypes: [],
  tplStatuses: [],
  duoStatuses: [],
  pictureRequired: false,
  pictureOptional: false,
  pictureTitle: '',
  textFieldRequired: false,
  textFieldOptional: false,
  textFieldTitle: '',
  supervisorNotificationWarning: false,
  supervisorAutomaticNotification: false,
  icon: '',
};

// ─── Icon helpers ─────────────────────────────────────────────────────────────

const PlusIcon = () => (
  <Svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth={2.4} strokeLinecap="round" />
  </Svg>
);
const InboxIcon = () => (
  <Svg width={34} height={34} viewBox="0 0 24 24" fill="none">
    <Path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 14H6l2.5-3 1.8 2.1 2.7-3.6L16 17zm1-8H4V5h16v4z" fill="#E0E0E0" />
  </Svg>
);

const Toggle = ({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) => (
  <Pressable
    style={[tog.track, value && tog.trackOn]}
    onPress={() => onChange(!value)}
    accessibilityRole="switch"
    accessibilityState={{ checked: value }}
  >
    <View style={[tog.thumb, value && tog.thumbOn]} />
  </Pressable>
);
const tog = StyleSheet.create({
  track: { width: 38, height: 22, borderRadius: 100, backgroundColor: '#9E9E9E', justifyContent: 'center', paddingHorizontal: 2 },
  trackOn: { backgroundColor: '#027C06' },
  thumb: { width: 18, height: 18, borderRadius: 100, backgroundColor: '#fff', elevation: 1 },
  thumbOn: { marginLeft: 16 },
});

const FormToggleRow = ({ label, value, onChange, hint }: { label: string; value: boolean; onChange: (v: boolean) => void; hint?: string }) => (
  <View style={ftr.row}>
    <Toggle value={value} onChange={onChange} />
    <Text style={ftr.label}>{label}</Text>
    {hint ? <Text style={ftr.hint}> {hint}</Text> : null}
  </View>
);
const ftr = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  label: { fontSize: 14, fontWeight: '500', color: '#2D2D2D', fontFamily: FontFamily.medium },
  hint: { fontSize: 13, color: '#9E9E9E', fontFamily: FontFamily.regular },
});

// ─── Column definitions ───────────────────────────────────────────────────────

const renderTagsCell = (values: string[]) => (
  <View style={s.tagsCell}>
    {values.length === 0 ? (
      <Text style={s.tagEmptyText}>-</Text>
    ) : (
      <View style={s.tagWrap}>
        {values.map((value) => (
          <View key={value} style={s.tag}><Text style={s.tagText}>{value}</Text></View>
        ))}
      </View>
    )}
  </View>
);

const UNICODE_EMOJI_REGEX = /\p{Extended_Pictographic}/u;

const hasUnicodeEmoji = (value: string) => UNICODE_EMOJI_REGEX.test(value);

const renderIconValue = (value: string, size: number, color: string) => {
  if (hasUnicodeEmoji(value)) {
    return <Text style={[s.emojiIcon, { fontSize: size + 2 }]}>{value}</Text>;
  }
  return <MaterialIcons name={value as any} size={size} color={color} />;
};

const renderIconCell = (item: HaulbackReasonCode) => (
  <View style={{ justifyContent: 'center', alignItems: 'center', minHeight: 48, paddingHorizontal: 12 }}>
    {item.icon ? (
      renderIconValue(item.icon, 20, '#005CBC')
    ) : (
      <Text style={{ fontSize: 13, color: '#B1B1B1' }}>—</Text>
    )}
  </View>
);

// Columns are built per-language inside the component so they can use the selected language

// ─── Main component ───────────────────────────────────────────────────────────

const HaulbacksTab: React.FC<{ listLanguage?: EquipmentLanguage }> = ({ listLanguage = 'en' }) => {
  const toast = useToast();
  
  // Load org types and location types from Redux masterdata
  const locationTypes = useSelector((state: RootState) => state.masterData.locationFacilityTypes);
  const orgTypes = useSelector((state: RootState) => state.masterData.orgTypes);

  const [records, setRecords] = useState<HaulbackReasonCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: SortDir }>({
    key: 'displayOrder',
    direction: 'asc',
  });
  const [colFilters, setColFilters] = useState<ColFilters>({});
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [isIconDropdownVisible, setIsIconDropdownVisible] = useState(false);
  const [modalLanguage, setModalLanguage] = useState<EquipmentLanguage>('en');
  const [enForm, setEnForm] = useState({ reasonCode: '', pictureLabel: '', textLabel: '' });
  const [frForm, setFrForm] = useState({ reasonCode: '', pictureLabel: '', textLabel: '' });
  const [esForm, setEsForm] = useState({ reasonCode: '', pictureLabel: '', textLabel: '' });
  const [showTranslationError, setShowTranslationError] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const COLS = [
    {
      columnKey: 'reasonCode',
      header: t(listLanguage, 'table.reasonCode'),
      flexGrow: 2,
    },
    {
      columnKey: 'icon',
      header: t(listLanguage, 'table.icon'),
      flexGrow: 0.6,
      sortable: false,
      renderCell: (item: HaulbackReasonCode) => renderIconCell(item),
    },
    {
      columnKey: 'displayOrder',
      header: t(listLanguage, 'table.displayOrder'),
      flexGrow: 1,
      getSortValue: (item: HaulbackReasonCode) => item.displayOrder,
    },
    {
      columnKey: 'locationTypes',
      header: t(listLanguage, 'table.locationType'),
      flexGrow: 1,
      renderCell: (item: HaulbackReasonCode) => renderTagsCell(item.locationTypes),
      getSortValue: (item: HaulbackReasonCode) => item.locationTypes[0] ?? '',
    },
    {
      columnKey: 'orgTypes',
      header: t(listLanguage, 'table.orgType'),
      flexGrow: 1,
      renderCell: (item: HaulbackReasonCode) => renderTagsCell(item.orgTypes),
      getSortValue: (item: HaulbackReasonCode) => item.orgTypes[0] ?? '',
    },
    {
      columnKey: 'tplStatuses',
      header: t(listLanguage, 'table.3pl'),
      flexGrow: 1,
      renderCell: (item: HaulbackReasonCode) => renderTagsCell(item.tplStatuses),
      getSortValue: (item: HaulbackReasonCode) => item.tplStatuses[0] ?? '',
    },
    {
      columnKey: 'duoStatuses',
      header: t(listLanguage, 'table.duo'),
      flexGrow: 1,
      renderCell: (item: HaulbackReasonCode) => renderTagsCell(item.duoStatuses),
      getSortValue: (item: HaulbackReasonCode) => item.duoStatuses[0] ?? '',
    },
    {
      columnKey: 'active',
      header: t(listLanguage, 'table.active'),
      flexGrow: 1,
      getSortValue: (item: HaulbackReasonCode) => (item.active ? 1 : 0),
    },
  ];

  // ── Load records from API with server-side sorting ────────────────────────
  const loadRecords = useCallback(async (sort: { key: string; direction: SortDir }) => {
    setLoading(true);
    try {
      const backendField = HAULBACK_SORT_FIELD_MAP[sort.key];
      const sortBy = backendField
        ? sort.direction === 'desc' ? `-${backendField}` : backendField
        : undefined;
      const params: Record<string, string> = { language: listLanguage };
      if (sortBy) params.sortBy = sortBy;
      const result = await fetchHaulbacks(params);
      setRecords(result.items.map(haulbackFromDTO));
    } catch (err) {
      toast.showToast({ header: 'Error', subtext: parseError(err), variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [listLanguage]);

  // Reload the list (labels re-resolve server-side) whenever the selected language changes.
  useEffect(() => { loadRecords(sortConfig); }, [loadRecords]);

  const getFilterOptions = (col: string): string[] => {
    const vals = new Set<string>();
    records.forEach((r) => {
      const v = r[col as keyof HaulbackReasonCode];
      if (col === 'active') { vals.add(r.active ? 'Active' : 'Disabled'); return; }
      if (Array.isArray(v)) { (v as string[]).forEach((i) => vals.add(i)); return; }
      vals.add(String(v));
    });
    return [...vals].sort();
  };

  const handleToggleActive = async (id: string, _val: boolean) => {
    try {
      const updated = await toggleHaulback(Number(id));
      setRecords((prev) => prev.map((r) => (r.id === id ? haulbackFromDTO(updated) : r)));
    } catch (err) {
      toast.showToast({ header: 'Error', subtext: parseError(err), variant: 'error' });
    }
  };

  const tableColumns = useMemo(() => {
    const getOptions = (values: string[]) => values.map((value) => ({ label: value, value }));

    return COLS.map((col) => ({
      ...col,
      sortable: col.columnKey !== 'active',
      filterOptions: getOptions(getFilterOptions(col.columnKey)),
      renderCell: col.columnKey === 'active'
        ? (item: HaulbackReasonCode) => (
            <View style={s.toggleCell}>
              <Toggle value={item.active} onChange={(value) => handleToggleActive(item.id, value)} />
              <Text style={[s.toggleLabel, item.active && s.toggleLabelOn]}>
                {item.active ? t(listLanguage, 'label.active') : t(listLanguage, 'label.disabled')}
              </Text>
            </View>
          )
        : col.renderCell,
    }));
  }, [records, COLS, listLanguage]);

  const filteredRecords = useMemo(() => {
    let rows = [...records];
    Object.entries(colFilters).forEach(([col, sel]) => {
      if (sel.length === 0) return;
      rows = rows.filter((row) => {
        const val = row[col as keyof HaulbackReasonCode];
        if (col === 'active') {
          return sel.includes(row.active ? 'Active' : 'Disabled');
        }
        if (Array.isArray(val)) {
          return (val as string[]).some((v) => sel.includes(v));
        }
        return sel.includes(String(val));
      });
    });
    return rows;
  }, [records, colFilters]);

  // Keep current page within range when filtered records change
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [filteredRecords, page, pageSize]);

  const pagedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  const sortedCount = filteredRecords.length;

  const anyFilterActive = Object.values(colFilters).some((v) => v.length > 0);

  const normalize = (v: string) => v.trim().toLowerCase();

  const filteredIconOptions = useMemo(() => {
    const query = normalize(form.icon);
    if (!query) return DELIVERY_RPM_ICON_OPTIONS;
    return DELIVERY_RPM_ICON_OPTIONS.filter((iconName) => normalize(iconName).includes(query));
  }, [form.icon]);

  // On create, the reason code is entered per-language (enForm/frForm/esForm); the English
  // value is what's persisted as the canonical `reasonCode`, so validation/duplication
  // checks must read from `enForm` rather than the (now unused-for-create) `form.reasonCode`.
  const normalizedReasonCode = (modalMode === 'create' ? enForm.reasonCode : form.reasonCode).trim().toLowerCase();
  const parsedDisplayOrder = form.displayOrder.trim() !== '' ? Number(form.displayOrder) : null;

  const isReasonCodeDuplicate =
    modalMode === 'create' &&
    normalizedReasonCode !== '' &&
    records.some((r) => r.reasonCode.trim().toLowerCase() === normalizedReasonCode);

  const isDisplayOrderDuplicate = (() => {
    if (parsedDisplayOrder == null) return false;
    if (modalMode === 'create') {
      return records.some((r) => r.displayOrder === parsedDisplayOrder);
    }
    if (modalMode === 'edit' && editId) {
      return records.some((r) => r.displayOrder === parsedDisplayOrder && r.id !== editId);
    }
    return false;
  })();

  // Mutual exclusion: pictureRequired and pictureOptional cannot both be true
  const isPictureMutuallyExclusive = form.pictureRequired && form.pictureOptional;
  // Mutual exclusion: textFieldRequired and textFieldOptional cannot both be true
  const isTextFieldMutuallyExclusive = form.textFieldRequired && form.textFieldOptional;

  // Field label validation: if photo or text field is enabled, check if all 3 language labels are filled
  const arePhotoLabelsComplete =
    !form.pictureRequired && !form.pictureOptional
      ? true // not required if field is disabled
      : (enForm?.pictureLabel ?? '').trim() !== '' &&
        (frForm?.pictureLabel ?? '').trim() !== '' &&
        (esForm?.pictureLabel ?? '').trim() !== '';

  const areTextLabelsComplete =
    !form.textFieldRequired && !form.textFieldOptional
      ? true // not required if field is disabled
      : (enForm?.textLabel ?? '').trim() !== '' &&
        (frForm?.textLabel ?? '').trim() !== '' &&
        (esForm?.textLabel ?? '').trim() !== '';

  // Current-language label values (defensive fallback avoids crashes if a form object is ever partial)
  const currentPictureLabel = modalLanguage === 'en' ? (enForm?.pictureLabel ?? '') : modalLanguage === 'fr' ? (frForm?.pictureLabel ?? '') : (esForm?.pictureLabel ?? '');
  const currentTextLabel = modalLanguage === 'en' ? (enForm?.textLabel ?? '') : modalLanguage === 'fr' ? (frForm?.textLabel ?? '') : (esForm?.textLabel ?? '');

  const isFormValid = modalMode === 'edit'
    ? form.icon.trim() !== '' &&
      form.displayOrder.trim() !== '' &&
      !isDisplayOrderDuplicate &&
      form.locationTypes.length > 0 &&
      form.orgTypes.length > 0 &&
      form.tplStatuses.length > 0 &&
      form.duoStatuses.length > 0 &&
      !isPictureMutuallyExclusive &&
      !isTextFieldMutuallyExclusive
    : enForm.reasonCode.trim() !== '' &&
      form.displayOrder.trim() !== '' &&
      form.icon.trim() !== '' &&
      form.locationTypes.length > 0 &&
      form.orgTypes.length > 0 &&
      form.tplStatuses.length > 0 &&
      form.duoStatuses.length > 0 &&
      !isReasonCodeDuplicate &&
      !isDisplayOrderDuplicate &&
      !isPictureMutuallyExclusive &&
      !isTextFieldMutuallyExclusive;

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleSort = (key: string, direction: SortDir) => {
    const next = { key, direction };
    setSortConfig(next);
    setPage(1);
    loadRecords(next);
  };

  const handleColumnFilter = (columnKey: string, values: string[]) => {
    setColFilters((prev) => ({ ...prev, [columnKey]: values }));
    setPage(1);
  };

  const clearAllFilters = () => { setColFilters({}); setPage(1); };

  const handleOpenCreate = () => {
    setForm(EMPTY_FORM);
    setSubmitError(null);
    setEditId(null);
    setModalMode('create');
    setModalLanguage('en');
    setEnForm({ reasonCode: '', pictureLabel: '', textLabel: '' });
    setFrForm({ reasonCode: '', pictureLabel: '', textLabel: '' });
    setEsForm({ reasonCode: '', pictureLabel: '', textLabel: '' });
    setShowTranslationError(false);
    setAttemptedSubmit(false);
  };

  const handleOpenEdit = (rec: HaulbackReasonCode) => {
    setForm({
      reasonCode: rec.reasonCode,
      displayOrder: String(rec.displayOrder),
      reasonCodeCategory: rec.reasonCodeCategory,
      locationTypes: [...rec.locationTypes],
      orgTypes: [...rec.orgTypes],
      tplStatuses: [...rec.tplStatuses],
      duoStatuses: [...rec.duoStatuses],
      pictureRequired: rec.pictureRequired,
      pictureOptional: rec.pictureOptional,
      pictureTitle: rec.pictureTitle,
      textFieldRequired: rec.textFieldRequired,
      textFieldOptional: rec.textFieldOptional,
      textFieldTitle: rec.textFieldTitle,
      supervisorNotificationWarning: rec.supervisorNotificationWarning,
      supervisorAutomaticNotification: rec.supervisorAutomaticNotification,
      icon: rec.icon,
    });
    setSubmitError(null);
    setEditId(rec.id);
    setModalMode('edit');
    setAttemptedSubmit(false);
    // Initialize language forms with fallback values
    setEnForm({ reasonCode: rec.reasonCode, pictureLabel: '', textLabel: '' });
    setFrForm({ reasonCode: rec.reasonCode, pictureLabel: '', textLabel: '' });
    setEsForm({ reasonCode: rec.reasonCode, pictureLabel: '', textLabel: '' });
    setModalLanguage(listLanguage);
    (async () => {
      try {
        const [en, fr, es] = await Promise.all([
          fetchHaulbackById(Number(rec.id), 'en'),
          fetchHaulbackById(Number(rec.id), 'fr'),
          fetchHaulbackById(Number(rec.id), 'es'),
        ]);
        setEnForm({ reasonCode: en.reasonCode, pictureLabel: en.pictureTitle || '', textLabel: en.textFieldTitle || '' });
        setFrForm({ reasonCode: fr.reasonCode, pictureLabel: fr.pictureTitle || '', textLabel: fr.textFieldTitle || '' });
        setEsForm({ reasonCode: es.reasonCode, pictureLabel: es.pictureTitle || '', textLabel: es.textFieldTitle || '' });
      } catch {
        // Keep the seeded fallback values if per-language fetch fails.
      }
    })();
  };

  const handleSave = async () => {
    setAttemptedSubmit(true);
    if (!isFormValid) return;
    if (!arePhotoLabelsComplete || !areTextLabelsComplete) return;
    setSubmitError(null);
    // On create, require translations for all languages
    if (modalMode === 'create') {
      if (!enForm.reasonCode.trim() || !frForm.reasonCode.trim() || !esForm.reasonCode.trim()) {
        setShowTranslationError(true);
        return;
      }
    }
    try {
      const translations = [
        { languageCode: 'en', label: enForm.reasonCode.trim() },
        { languageCode: 'fr', label: frForm.reasonCode.trim() },
        { languageCode: 'es', label: esForm.reasonCode.trim() },
      ];

      // Build field label translations only if photo/text fields are enabled
      const fieldLabelTranslations = [];
      if (form.pictureRequired || form.pictureOptional) {
        fieldLabelTranslations.push(
          { languageCode: 'en', pictureTitleLabel: (enForm.pictureLabel ?? '').trim() },
          { languageCode: 'fr', pictureTitleLabel: (frForm.pictureLabel ?? '').trim() },
          { languageCode: 'es', pictureTitleLabel: (esForm.pictureLabel ?? '').trim() }
        );
      }
      if (form.textFieldRequired || form.textFieldOptional) {
        fieldLabelTranslations.push(
          { languageCode: 'en', textFieldTitleLabel: (enForm.textLabel ?? '').trim() },
          { languageCode: 'fr', textFieldTitleLabel: (frForm.textLabel ?? '').trim() },
          { languageCode: 'es', textFieldTitleLabel: (esForm.textLabel ?? '').trim() }
        );
      }

      if (modalMode === 'create') {
        const payload = {
          translations,
          ...(fieldLabelTranslations.length > 0 && { fieldLabelTranslations }),
          reasonCodeCategory: form.reasonCodeCategory,
          iconName: form.icon || undefined,
          displayOrder: Number(form.displayOrder),
          locationTypes: form.locationTypes,
          orgTypes: form.orgTypes,
          threePlStatuses: form.tplStatuses,
          duoStatuses: form.duoStatuses,
          pictureRequired: form.pictureRequired,
          pictureOptional: form.pictureOptional,
          pictureTitle: form.pictureTitle.trim(),
          textFieldRequired: form.textFieldRequired,
          textFieldOptional: form.textFieldOptional,
          textFieldTitle: form.textFieldTitle.trim(),
          supervisorNotificationWarning: form.supervisorNotificationWarning,
          supervisorAutomaticNotification: form.supervisorAutomaticNotification,
        };
        const dto = await createHaulback(payload);
        setRecords(prev => [...prev, haulbackFromDTO(dto)]);
        toast.showToast({ header: t(listLanguage, 'toast.addSuccess'), subtext: '', variant: 'success' });
      } else if (modalMode === 'edit' && editId) {
        const payload = {
          translations,
          ...(fieldLabelTranslations.length > 0 && { fieldLabelTranslations }),
          reasonCodeCategory: form.reasonCodeCategory,
          iconName: form.icon || undefined,
          displayOrder: Number(form.displayOrder),
          locationTypes: form.locationTypes,
          orgTypes: form.orgTypes,
          threePlStatuses: form.tplStatuses,
          duoStatuses: form.duoStatuses,
          pictureRequired: form.pictureRequired,
          pictureOptional: form.pictureOptional,
          pictureTitle: form.pictureTitle.trim(),
          textFieldRequired: form.textFieldRequired,
          textFieldOptional: form.textFieldOptional,
          textFieldTitle: form.textFieldTitle.trim(),
          supervisorNotificationWarning: form.supervisorNotificationWarning,
          supervisorAutomaticNotification: form.supervisorAutomaticNotification,
        };
        const dto = await updateHaulback(Number(editId), payload);
        setRecords(prev => prev.map(r => r.id === editId ? haulbackFromDTO(dto) : r));
        toast.showToast({ header: t(listLanguage, 'toast.updateSuccess'), subtext: '', variant: 'success' });
      }
      setModalMode(null);
    } catch (err) {
      const message = parseError(err);
      setSubmitError(message);
      toast.showToast({ header: 'Error', subtext: parseError(err), variant: 'error' });
    }
  };

  const handleCancelClick = () => setShowCancelConfirm(true);
  const handleConfirmDiscard = () => { setShowCancelConfirm(false); setSubmitError(null); setModalMode(null); };
  const handleDismissConfirm = () => setShowCancelConfirm(false);

  const updateForm = (patch: Partial<FormState>) => {
    if (submitError) setSubmitError(null);
    setForm(prev => ({ ...prev, ...patch }));
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <View style={s.root}>
      {/* Top bar */}
      <View style={s.topBar}>
        <Text style={s.summary}>
          {t(listLanguage, 'summary.showing').replace('{countFiltered}', String(sortedCount)).replace('{countTotal}', String(records.length))}
        </Text>
        <View style={s.topActions}>
          {anyFilterActive && (
            <Pressable style={s.clearBtn} onPress={clearAllFilters}>
              <Text style={s.clearBtnText}>{t(listLanguage, 'button.clearFilters')}</Text>
            </Pressable>
          )}
          <Pressable style={s.createBtn} onPress={handleOpenCreate}>
            <PlusIcon />
            <Text style={s.createBtnText}>{t(listLanguage, 'addButton.haulback')}</Text>
          </Pressable>
        </View>
      </View>

      <View style={s.tableWrap}>
        {!loading && filteredRecords.length === 0 ? (
          <View style={s.emptyWrap}>
            <InboxIcon />
            <Text style={s.emptyText}>
              {anyFilterActive
                ? t(listLanguage, 'empty.noRecordsFilters')
                : t(listLanguage, 'empty.noRecords')}
            </Text>
            {anyFilterActive && (
              <Pressable style={s.emptyBtn} onPress={clearAllFilters}>
                <Text style={s.emptyBtnText}>{t(listLanguage, 'button.clearFilters')}</Text>
              </Pressable>
            )}
          </View>
        ) : (
          <HarborDataTable
            data={pagedRecords}
            columns={tableColumns}
            tableHeader={t(listLanguage, 'tabs.haulbacks')}
            tableDescription={t(listLanguage, 'tabs.haulbacks')}
            maxRecordsToShow={pageSize}
            onRowClick={handleOpenEdit}
            onSort={handleSort}
            externalPage={page - 1}
            externalTotalPages={Math.max(1, Math.ceil(filteredRecords.length / pageSize))}
            onExternalPageChange={(nextPage) => setPage(nextPage + 1)}
            onPageSizeChange={(nextSize) => {
              setPageSize(nextSize);
              setPage(1);
            }}
            showHeaderTopBorder={false}
            columnFilters={colFilters}
            onColumnFilter={handleColumnFilter}
            isLoading={loading}
            locale={{
              rowsPerPageLabel: t(listLanguage, 'pagination.rowsPerPage'),
              pageLabel: t(listLanguage, 'pagination.page') ?? 'Page',
              pageOfLabel: t(listLanguage, 'pagination.of') ?? 'of',
              firstPageLabel: t(listLanguage, 'pagination.first') ?? 'First page',
              prevPageLabel: t(listLanguage, 'pagination.previous') ?? 'Previous page',
              nextPageLabel: t(listLanguage, 'pagination.next') ?? 'Next page',
              lastPageLabel: t(listLanguage, 'pagination.last') ?? 'Last page',
            }}
          />
        )}
      </View>

      {/* ── Create / Edit Modal ── */}
      <Modal visible={modalMode !== null} transparent animationType="fade">
        <View style={s.backdrop}>
          <View style={s.formModal}>
            {/* Header */}
            <View style={s.modalHeader}>
              <Text style={s.modalTitle}>
                {modalMode === 'create' ? t(modalLanguage, 'modal.addTitle') : t(modalLanguage, 'modal.editTitle')}
              </Text>
              <Pressable style={s.modalClose} onPress={handleCancelClick}>
                <Text style={s.modalCloseText}>✕</Text>
              </Pressable>
            </View>

            {/* Body */}
            <ScrollView style={s.modalBodyScroll} contentContainerStyle={s.modalBody} showsVerticalScrollIndicator={false}>
              {/* Modal language toggle + Reason Code */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <View style={{ flex: 1 }}>
                  {showTranslationError ? (
                    <View style={[s.modalErrorBox]}>
                      <MaterialIcons name="error-outline" size={16} color={AdminColors.red400} />
                      <Text style={s.modalErrorText}>{t(modalLanguage, 'validation.completeTranslations')}</Text>
                    </View>
                  ) : null}
                </View>
                <View style={{ marginLeft: 12 }}>
                  <ListLanguageToggle value={modalLanguage} onChange={(l) => { setModalLanguage(l); setShowTranslationError(false); }} compact />
                </View>
              </View>

              <View style={s.field}>
                <View style={s.fieldLabelRow}>
                  <Text style={s.fieldLabel}>{t(modalLanguage, 'modal.form.reasonCode')}</Text>
                  <Text style={s.req}> *</Text>
                </View>
                {modalMode === 'edit' ? (
                  <View style={s.readonlyField}>
                    <Text style={s.readonlyText}>
                      {modalLanguage === 'en' ? enForm.reasonCode : modalLanguage === 'fr' ? frForm.reasonCode : esForm.reasonCode}
                    </Text>
                  </View>
                ) : (
                  <TextInput
                    style={[s.textInput, isReasonCodeDuplicate && s.textInputError]}
                    value={modalLanguage === 'en' ? enForm.reasonCode : modalLanguage === 'fr' ? frForm.reasonCode : esForm.reasonCode}
                    onChangeText={t => {
                      if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, reasonCode: t }));
                      else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, reasonCode: t }));
                      else setEsForm(prev => ({ ...prev, reasonCode: t }));
                      setSubmitError(null);
                      setShowTranslationError(false);
                    }}
                    placeholder={t(modalLanguage, 'modal.form.placeholder')}
                    placeholderTextColor="#B1B1B1"
                  />
                )}
                {modalMode === 'create' && isReasonCodeDuplicate && (
                  <Text style={s.fieldErrorText}>{t(modalLanguage, 'validation.reasonCodeDuplicate')}</Text>
                )}
              </View>

              {/* Display Order */}
              <View style={s.field}>
                <View style={s.fieldLabelRow}>
                  <Text style={s.fieldLabel}>{t(modalLanguage, 'modal.form.displayOrder')}</Text>
                  <Text style={s.req}> *</Text>
                </View>
                <TextInput
                  style={[s.textInput, isDisplayOrderDuplicate && s.textInputError]}
                  value={form.displayOrder}
                  onChangeText={t => updateForm({ displayOrder: t.replace(/\D/g, '') })}
                  placeholder="e.g. 1"
                  placeholderTextColor="#B1B1B1"
                  keyboardType="numeric"
                />
                {isDisplayOrderDuplicate && (
                  <Text style={s.fieldErrorText}>{t(modalLanguage, 'validation.displayOrderDuplicate')}</Text>
                )}
              </View>

              {/* Reason Code Category */}
              <View style={s.field}>
                <View style={s.fieldLabelRow}>
                  <Text style={s.fieldLabel}>{t(modalLanguage, 'modal.form.reasonCodeCategory')}</Text>
                  <Text style={s.req}> *</Text>
                </View>
                <View style={s.typeOpts}>
                  {REASON_CODE_CATEGORIES.map((category) => (
                    <Pressable
                      key={category}
                      style={[s.typeOpt, form.reasonCodeCategory === category && s.typeOptSel]}
                      onPress={() => updateForm({ reasonCodeCategory: category as any })}
                    >
                      <Text
                        style={[
                          s.typeOptText,
                          form.reasonCodeCategory === category && s.typeOptTextSel,
                        ]}
                      >
                        {t(modalLanguage, `category.${category.toLowerCase()}` as any)}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              </View>

              {/* Multi-selects */}
              <MultiSelectDropdown
                label={t(modalLanguage, 'modal.form.locationType')}
                required
                options={locationTypes}
                selected={form.locationTypes}
                onChange={v => updateForm({ locationTypes: v })}
                placeholder={t(modalLanguage, 'modal.form.selectPlaceholder')}
                selectAllLabel={t(modalLanguage, 'modal.form.selectAll')}
                clearAllLabel={t(modalLanguage, 'modal.form.clearAll')}
              />
              <MultiSelectDropdown
                label={t(modalLanguage, 'modal.form.orgType')}
                required
                options={orgTypes}
                selected={form.orgTypes}
                onChange={v => updateForm({ orgTypes: v })}
                placeholder={t(modalLanguage, 'modal.form.selectPlaceholder')}
                selectAllLabel={t(modalLanguage, 'modal.form.selectAll')}
                clearAllLabel={t(modalLanguage, 'modal.form.clearAll')}
              />
              <MultiSelectDropdown
                label={t(modalLanguage, 'modal.form.3plStatus')}
                required
                options={TPL_STATUSES}
                selected={form.tplStatuses}
                onChange={v => updateForm({ tplStatuses: v })}
                placeholder={t(modalLanguage, 'modal.form.selectPlaceholder')}
                selectAllLabel={t(modalLanguage, 'modal.form.selectAll')}
                clearAllLabel={t(modalLanguage, 'modal.form.clearAll')}
              />
              <MultiSelectDropdown
                label={t(modalLanguage, 'modal.form.duoStatus')}
                required
                options={DUO_STATUSES}
                selected={form.duoStatuses}
                onChange={v => updateForm({ duoStatuses: v })}
                placeholder={t(modalLanguage, 'modal.form.selectPlaceholder')}
                selectAllLabel={t(modalLanguage, 'modal.form.selectAll')}
                clearAllLabel={t(modalLanguage, 'modal.form.clearAll')}
              />

              {/* Icon */}
              <View style={s.field}>
                <View style={s.fieldLabelRow}>
                  <Text style={s.fieldLabel}>{t(modalLanguage, 'modal.form.icon')}</Text>
                  <Text style={s.req}> *</Text>
                </View>
                <View style={s.iconInputRow}>
                  <View style={s.iconPreviewWrap}>
                    {renderIconValue(form.icon || 'image', 18, '#005CBC')}
                  </View>
                  <TextInput
                    style={[s.textInput, s.flex1]}
                    value={form.icon}
                    onChangeText={t => updateForm({ icon: t })}
                    placeholder={t(modalLanguage, 'modal.form.iconPlaceholder')}
                    placeholderTextColor="#B1B1B1"
                    autoCapitalize="none"
                    autoCorrect={false}
                    onFocus={() => setIsIconDropdownVisible(true)}
                    onBlur={() => { setTimeout(() => setIsIconDropdownVisible(false), 200); }}
                  />
                </View>
                {isIconDropdownVisible ? (
                  <View style={s.iconDropdownMenu}>
                    {filteredIconOptions.length > 0 ? (
                      <ScrollView style={s.iconDropdownList} keyboardShouldPersistTaps="handled">
                        {filteredIconOptions.map((iconName) => {
                          const selected = form.icon.trim().toLowerCase() === iconName.trim().toLowerCase();
                          return (
                            <Pressable
                              key={iconName}
                              style={[s.iconDropdownOption, selected && s.iconDropdownOptionActive]}
                              onPressIn={() => {
                                updateForm({ icon: iconName });
                                setIsIconDropdownVisible(false);
                              }}
                            >
                              <MaterialIcons name={iconName as any} size={18} color={selected ? '#005CBC' : '#4B4B4B'} />
                              <Text style={[s.iconDropdownOptionText, selected && s.iconDropdownOptionTextActive]}>
                                {iconName}
                              </Text>
                            </Pressable>
                          );
                        })}
                      </ScrollView>
                    ) : (
                      <Text style={s.iconDropdownEmptyText}>{t(modalLanguage, 'modal.form.iconEmptyHint')}</Text>
                    )}
                  </View>
                ) : null}
              </View>

              {/* Optional Additional Fields */}
              <View style={s.optionalSection}>
                <Text style={s.optionalHeader}>Optional Additional Fields</Text>

                {/* Photo Field */}
                <View style={s.toggleGroup}>
                  <Text style={s.toggleGroupLabel}>Photo Field</Text>
                  <View style={s.toggleGroupRows}>
                    <FormToggleRow
                      label="Required"
                      value={form.pictureRequired}
                      onChange={v => updateForm({ pictureRequired: v, ...(v ? { pictureOptional: false } : {}) })}
                    />
                    <FormToggleRow
                      label="Optional"
                      value={form.pictureOptional}
                      onChange={v => updateForm({ pictureOptional: v, ...(v ? { pictureRequired: false } : {}) })}
                    />
                  </View>
                  {isPictureMutuallyExclusive && (
                    <Text style={s.fieldErrorText}>Photo field cannot be both Required and Optional.</Text>
                  )}
                  {(form.pictureRequired || form.pictureOptional) && (
                    <View style={s.titleInputWrap}>
                      <Text style={s.titleInputLabel}>{t(modalLanguage, 'modal.form.photoFieldLabel')}</Text>
                      <TextInput
                        style={[
                          s.textInput,
                          attemptedSubmit && currentPictureLabel.trim() === '' &&
                          (form.pictureRequired || form.pictureOptional) &&
                          s.textInputError
                        ]}
                        value={currentPictureLabel}
                        onChangeText={t => {
                          if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, pictureLabel: t }));
                          else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, pictureLabel: t }));
                          else setEsForm(prev => ({ ...prev, pictureLabel: t }));
                        }}
                        placeholder={modalLanguage === 'en' ? 'e.g. Attach Issue Photo' : modalLanguage === 'fr' ? 'ex. Joindre une photo du problème' : 'p.ej. Adjuntar foto del problema'}
                        placeholderTextColor="#B1B1B1"
                        maxLength={200}
                      />
                      {attemptedSubmit && !arePhotoLabelsComplete && (form.pictureRequired || form.pictureOptional) && (
                        <Text style={s.fieldErrorText}>{t(modalLanguage, 'validation.fieldLabelAllLanguagesRequired')}</Text>
                      )}
                    </View>
                  )}
                </View>

                {/* Text Field */}
                <View style={s.toggleGroup}>
                  <Text style={s.toggleGroupLabel}>Text Field</Text>
                  <View style={s.toggleGroupRows}>
                    <FormToggleRow
                      label="Required"
                      value={form.textFieldRequired}
                      onChange={v => updateForm({ textFieldRequired: v, ...(v ? { textFieldOptional: false } : {}) })}
                    />
                    <FormToggleRow
                      label="Optional"
                      value={form.textFieldOptional}
                      onChange={v => updateForm({ textFieldOptional: v, ...(v ? { textFieldRequired: false } : {}) })}
                    />
                  </View>
                  {isTextFieldMutuallyExclusive && (
                    <Text style={s.fieldErrorText}>Text field cannot be both Required and Optional.</Text>
                  )}
                  {(form.textFieldRequired || form.textFieldOptional) && (
                    <View style={s.titleInputWrap}>
                      <Text style={s.titleInputLabel}>{t(modalLanguage, 'modal.form.textFieldLabel')}</Text>
                      <TextInput
                        style={[
                          s.textInput,
                          attemptedSubmit && currentTextLabel.trim() === '' &&
                          (form.textFieldRequired || form.textFieldOptional) &&
                          s.textInputError
                        ]}
                        value={currentTextLabel}
                        onChangeText={t => {
                          if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, textLabel: t }));
                          else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, textLabel: t }));
                          else setEsForm(prev => ({ ...prev, textLabel: t }));
                        }}
                        placeholder={modalLanguage === 'en' ? 'e.g. Describe the Issue' : modalLanguage === 'fr' ? 'ex. Décrivez le problème' : 'p.ej. Describe el problema'}
                        placeholderTextColor="#B1B1B1"
                        maxLength={200}
                      />
                      {attemptedSubmit && !areTextLabelsComplete && (form.textFieldRequired || form.textFieldOptional) && (
                        <Text style={s.fieldErrorText}>{t(modalLanguage, 'validation.fieldLabelAllLanguagesRequired')}</Text>
                      )}
                    </View>
                  )}
                </View>

                {/* Supervisor Notifications */}
                <View style={s.toggleGroup}>
                  <Text style={s.toggleGroupLabel}>Supervisor Notifications</Text>
                  <View style={s.toggleGroupRows}>
                    <FormToggleRow
                      label="Notification Warning"
                      value={form.supervisorNotificationWarning}
                      onChange={v => updateForm({
                        supervisorNotificationWarning: v,
                        ...(v ? { supervisorAutomaticNotification: false } : {}),
                      })}
                    />
                    <FormToggleRow
                      label="Automatic Notification"
                      value={form.supervisorAutomaticNotification}
                      onChange={v => updateForm({
                        supervisorAutomaticNotification: v,
                        ...(v ? { supervisorNotificationWarning: false } : {}),
                      })}
                    />
                  </View>
                </View>
                <Text style={s.optionalHeader}>{t(modalLanguage, 'modal.form.optionalFields')}</Text>
                <FormToggleRow
                  label={t(modalLanguage, 'modal.form.pictureRequirement')}
                  value={form.pictureRequired}
                  onChange={v => updateForm({ pictureRequired: v })}
                  hint={t(modalLanguage, 'modal.form.noByDefault')}
                />
                <FormToggleRow
                  label={t(modalLanguage, 'modal.form.textField')}
                  value={form.textFieldRequired}
                  onChange={v => updateForm({ textFieldRequired: v })}
                  hint={t(modalLanguage, 'modal.form.noByDefault')}
                />
              </View>

              {submitError ? (
                <View style={s.modalErrorBox}>
                  <Text style={s.modalErrorText}>{submitError}</Text>
                </View>
              ) : null}
            </ScrollView>

            {/* Footer */}
            <View style={s.modalFooter}>
              <Pressable style={s.btnSecondary} onPress={handleCancelClick}>
                <Text style={s.btnSecondaryText}>{t(modalLanguage, 'button.cancel')}</Text>
              </Pressable>
              <Pressable
                style={[s.btnPrimary, !isFormValid && s.btnPrimaryDisabled]}
                onPress={handleSave}
                disabled={!isFormValid}
              >
                <Text style={[s.btnPrimaryText, !isFormValid && s.btnPrimaryTextDisabled]}>{t(modalLanguage, 'button.save')}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Cancel Confirm Modal ── */}
      <ConfirmCancelModal
        visible={showCancelConfirm}
        onConfirmDiscard={handleConfirmDiscard}
        onGoBack={handleDismissConfirm}
        title={t(listLanguage, 'modal.unsaved.title')}
        message={t(listLanguage, 'modal.unsaved.message')}
        cancelLabel={t(listLanguage, 'modal.unsaved.cancel')}
        confirmLabel={t(listLanguage, 'modal.unsaved.confirm')}
      />
    </View>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: AdminColors.bgPage },

  // Top bar — design: .tbl-top flex row, justify space-between, padding 16px 20px 12px, flex-wrap, gap 14
  topBar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 14, paddingHorizontal: 20, paddingTop: 16, paddingBottom: 12,
  },
  // .tbl-summary — design: font-size 13 font-weight 600 color #616161
  summary: { fontSize: 13, fontWeight: '600', color: '#616161', fontFamily: FontFamily.medium },
  bold: { fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold },
  topActions: { flexDirection: 'row', alignItems: 'center', gap: 12 },

  // Clear filters — .btn-ghost design: font-size 14 font-weight 700 color #005CBC padding 6 10 border-radius 6
  clearBtn: { paddingHorizontal: 10, paddingVertical: 6, borderRadius: 6 },
  clearBtnText: { fontSize: 14, fontWeight: '700', color: '#005CBC', fontFamily: FontFamily.bold },

  // Create button — .btn.btn-primary design: height 40, padding 8px 24px, border-radius 24, font-size 16 font-weight 700
  createBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    height: 40, paddingHorizontal: 24, borderRadius: 24,
    backgroundColor: '#034895',
  },
  createBtnText: { fontSize: 16, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },

  // Table — design: .tbl-scroll overflow-x auto; .ntbl width 100% border-collapse
  tableWrap: { flex: 1, backgroundColor: '#fff' },
  tagsCell: { justifyContent: 'center', alignItems: 'flex-start', minHeight: 48, paddingHorizontal: 12, paddingVertical: 8, width: '100%' },
  tagWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, alignItems: 'flex-start' },
  tag: { backgroundColor: '#DAEEFA', borderRadius: 12, paddingHorizontal: 6, paddingVertical: 2 },
  tagText: { fontSize: 11, fontWeight: '500', color: '#01346F', fontFamily: FontFamily.medium },
  tagEmptyText: { fontSize: 13, color: '#838383', fontFamily: FontFamily.regular },
  toggleCell: { flexDirection: 'row', alignItems: 'center', gap: 8, minHeight: 48, paddingHorizontal: 12 },
  toggleLabel: { fontSize: 12, fontWeight: '500', color: '#838383', fontFamily: FontFamily.medium },
  toggleLabelOn: { color: '#027C06' },

  // Empty state — design: .tbl-empty padding 46px 24px text-center
  emptyWrap: { padding: 46, alignItems: 'center', gap: 14 },
  emptyText: { fontSize: 14, color: '#616161', textAlign: 'center', maxWidth: 400, fontFamily: FontFamily.regular },
  emptyBtn: { backgroundColor: '#034895', borderRadius: 24, paddingHorizontal: 24, paddingVertical: 8, marginTop: 4 },
  emptyBtnText: { fontSize: 16, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },

  // Backdrop / filter popover — design: .overlay bg rgba(0,0,0,.5); .fpop width 248 bg #fff border-radius 8 shadow elev-02
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  filterPanel: {
    width: '100%', maxWidth: 248, backgroundColor: '#fff', borderRadius: 8,
    overflow: 'hidden',
    ...Platform.select({ ios: { shadowColor: '#3A3A3A', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.08, shadowRadius: 24 }, android: { elevation: 4 } }),
  },
  // .fpop .fp-head — design: padding 10px 12px 8px, border-bottom 1px solid #EBEBEB
  filterPanelTitle: { fontSize: 13, fontWeight: '700', color: '#2D2D2D', paddingHorizontal: 12, paddingTop: 10, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: '#EBEBEB', fontFamily: FontFamily.bold },
  // .ms-acts — design: flex row gap 10, padding 8px 12px, border-bottom 1px #EBEBEB
  filterActions: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingTop: 8, paddingBottom: 8, gap: 10, borderBottomWidth: 1, borderBottomColor: '#EBEBEB' },
  filterActText: { fontSize: 12, fontWeight: '700', color: '#005CBC', fontFamily: FontFamily.bold },
  filterActDiv: { width: 1, height: 12, backgroundColor: '#E6E6E6', marginHorizontal: 0 },
  // .fp-item — design: padding 6px 10px font-size 13 border-radius 4
  filterItem: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 10, paddingVertical: 6 },
  // .fp-item .box — design: width 15, height 15, border 1.5px #838383, border-radius 3
  filterBox: { width: 15, height: 15, borderWidth: 1.5, borderColor: '#838383', borderRadius: 3, alignItems: 'center', justifyContent: 'center', backgroundColor: '#fff' },
  filterBoxOn: { backgroundColor: '#005CBC', borderColor: '#005CBC' },
  filterItemText: { fontSize: 13, color: '#2D2D2D', fontFamily: FontFamily.regular },
  // .fpop .fp-foot — design: flex row, justify space-between, padding 8px 12px, border-top 1px #EBEBEB
  filterFooter: { flexDirection: 'row', justifyContent: 'flex-end', gap: 8, paddingHorizontal: 12, paddingVertical: 8, borderTopWidth: 1, borderTopColor: '#EBEBEB' },
  filterCancelBtn: { paddingHorizontal: 14, paddingVertical: 6, borderRadius: 24, borderWidth: 1.5, borderColor: '#E6E6E6' },
  filterCancelText: { fontSize: 13, fontWeight: '600', color: '#616161', fontFamily: FontFamily.medium },
  filterApplyBtn: { paddingHorizontal: 14, paddingVertical: 6, borderRadius: 24, backgroundColor: '#005CBC' },
  filterApplyText: { fontSize: 13, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },

  // Create/Edit modal — design: .modal width 540 border-radius 8 shadow elev-03; .modal-h padding 16px 24px border-bottom; .modal-b padding 24px gap 18; .modal-f padding 16px 24px border-top
  formModal: {
    width: '100%', maxWidth: 540, maxHeight: '92%', backgroundColor: '#fff',
    borderRadius: 8, overflow: 'hidden',
    ...Platform.select({ ios: { shadowColor: 'rgba(0,94,192,1)', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.12, shadowRadius: 24 }, android: { elevation: 8 } }),
  },
  modalHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 24, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: '#EBEBEB',
  },
  modalTitle: { fontSize: 20, fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold },
  modalClose: { width: 30, height: 30, alignItems: 'center', justifyContent: 'center', borderRadius: 6 },
  modalCloseText: { fontSize: 16, color: '#616161' },
  // .modal-b — design: padding 24px, gap 18
  modalBodyScroll: { flex: 1 },
  modalBody: { padding: 24, gap: 18 },
  modalErrorBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: AdminColors.red75,
    borderRadius: 8,
    padding: 12,
  },
  modalErrorText: { fontSize: 13, color: AdminColors.red400, fontFamily: FontFamily.medium, flex: 1 },
  // .modal-f — design: padding 16px 24px, border-top, flex-end gap 16
  modalFooter: { flexDirection: 'row', justifyContent: 'flex-end', gap: 16, paddingHorizontal: 24, paddingVertical: 16, borderTopWidth: 1, borderTopColor: '#EBEBEB' },

  // Form fields — design: .field gap 8; .field label font-size 14 font-weight 700; .tin height 40 border 1px #D0D0D0 border-radius 6
  field: { gap: 8, marginBottom: 0 },
  fieldLabelRow: { flexDirection: 'row', alignItems: 'center' },
  fieldLabel: { fontSize: 14, fontWeight: '700', lineHeight: 20, color: '#2D2D2D', fontFamily: FontFamily.bold },
  req: { fontSize: 14, color: '#C12C01', fontFamily: FontFamily.bold },
  fieldHint: { fontSize: 12, color: '#838383', fontFamily: FontFamily.regular },
  textInput: {
    height: 40, borderWidth: 1, borderColor: '#D0D0D0', borderRadius: 6,
    paddingHorizontal: 12, fontSize: 14, color: '#2D2D2D', backgroundColor: '#fff',
    fontFamily: FontFamily.regular,
  },
  textInputError: { borderColor: '#C12C01' },
  fieldErrorText: { marginTop: 4, fontSize: 12, color: '#C12C01', fontFamily: FontFamily.medium },
  readonlyField: {
    height: 40, borderWidth: 1, borderColor: '#E8EAED', borderRadius: 6,
    paddingHorizontal: 12, justifyContent: 'center', backgroundColor: '#F9FAFB',
  },
  readonlyText: { fontSize: 14, color: '#616161', fontFamily: FontFamily.regular },

  // Optional section — design: .opt-subhead font-size 13 font-weight 700 color #616161 uppercase border-top padding-top 16
  optionalSection: { gap: 14, paddingTop: 16, borderTopWidth: 1, borderTopColor: '#F0F0F0' },
  optionalHeader: { fontSize: 13, fontWeight: '700', color: '#616161', textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: FontFamily.bold },
  toggleGroup: { gap: 10, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1, borderColor: '#F0F0F0', backgroundColor: '#FAFAFA' },
  toggleGroupLabel: { fontSize: 13, fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold },
  toggleGroupRows: { gap: 10 },
  titleInputWrap: { gap: 8, marginTop: 12 },
  titleInputLabel: { fontSize: 13, fontWeight: '700', color: '#2D2D2D', fontFamily: FontFamily.bold },
  titleInputHint: { fontSize: 12, color: '#838383', fontFamily: FontFamily.regular, marginTop: -4 },
  langInputGroup: { gap: 6 },
  langLabel: { fontSize: 12, fontWeight: '600', color: '#616161', fontFamily: FontFamily.medium },

  // Icon field
  flex1: { flex: 1 },
  iconInputRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  iconPreviewWrap: {
    width: 38, height: 38, borderRadius: 6, borderWidth: 1, borderColor: '#D0D0D0',
    backgroundColor: '#F9FAFB', alignItems: 'center', justifyContent: 'center',
  },
  iconDropdownMenu: {
    borderWidth: 1, borderColor: '#D0D0D0', borderRadius: 6,
    backgroundColor: '#fff', marginBottom: 8,
  },
  iconDropdownList: { maxHeight: 220 },
  iconDropdownOption: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: 10, paddingVertical: 8,
    borderBottomWidth: 1, borderBottomColor: '#EBEBEB',
  },
  iconDropdownOptionActive: { backgroundColor: '#F3F7FD' },
  iconDropdownOptionText: { fontSize: 12, color: '#4B4B4B', fontFamily: FontFamily.regular },
  iconDropdownOptionTextActive: { color: '#005CBC', fontFamily: FontFamily.medium },
  iconDropdownEmptyText: { fontSize: 12, color: '#838383', fontFamily: FontFamily.regular, paddingHorizontal: 10, paddingVertical: 10 },
  emojiIcon: { color: '#2D2D2D', lineHeight: 20 },

  // Category options — single select
  typeOpts: { flexDirection: 'row', gap: 10 },
  typeOpt: { flex: 1, paddingVertical: 10, borderRadius: 8, borderWidth: 1.5, borderColor: '#E0E0E0', alignItems: 'center' },
  typeOptSel: { backgroundColor: '#005CBC', borderColor: '#005CBC' },
  typeOptText: { fontSize: 14, fontWeight: '600', color: '#9E9E9E', fontFamily: FontFamily.medium },
  typeOptTextSel: { color: '#fff' },

  // Buttons — design: .btn height 40, padding 8px 24px, border-radius 24, font-size 16 font-weight 700
  btnSecondary: { height: 40, paddingHorizontal: 24, borderRadius: 24, borderWidth: 1, borderColor: '#034895', alignItems: 'center', justifyContent: 'center' },
  btnSecondaryText: { fontSize: 16, fontWeight: '700', color: '#034895', fontFamily: FontFamily.bold },
  btnPrimary: { height: 40, paddingHorizontal: 24, borderRadius: 24, backgroundColor: '#034895', alignItems: 'center', justifyContent: 'center' },
  btnPrimaryDisabled: { backgroundColor: '#E6E6E6', borderColor: '#E6E6E6' },
  btnPrimaryText: { fontSize: 16, fontWeight: '700', color: '#fff', fontFamily: FontFamily.bold },
  btnPrimaryTextDisabled: { color: '#838383' },
});

export default HaulbacksTab;
